/**
 * The talent dashboard's arithmetic, kept apart from the screens so every view
 * and every export counts the same way.
 *
 * Definitions HR settled on 9 Oct 2026:
 *  - In scope: every permanent and fixed-term employee in post, interns included
 *    (the server sends exactly these). People not yet placed still count.
 *  - The grid's shares are of the people placed on it; scope-level shares say so.
 *  - Bench strength = Ready Now people / approved positions (views 3 to 5).
 *  - No risk level on critical roles, and no "accelerated development journey",
 *    for now.
 */
import type {
  PlacementStatus,
  ReportingPerson,
  ReportingReadiness,
} from '@/services/api/talentReportingService'

export interface TalentFilters {
  function: string
  department: string
  team_1: string
  team_2: string
  team_3: string
  leadership_layer: string
  grade: string
  role: string
  critical: string
}

export type FilterKey = keyof TalentFilters

export const EMPTY_FILTERS: TalentFilters = {
  function: '',
  department: '',
  team_1: '',
  team_2: '',
  team_3: '',
  leadership_layer: '',
  grade: '',
  role: '',
  critical: '',
}

/** The filters HR asked for (9 Oct 2026), in the order the deck shows them. */
export const FILTER_FIELDS: { key: FilterKey; label: string }[] = [
  { key: 'function', label: 'Function' },
  { key: 'department', label: 'Department' },
  { key: 'team_1', label: 'Team 1' },
  { key: 'team_2', label: 'Team 2' },
  { key: 'team_3', label: 'Team 3' },
  { key: 'leadership_layer', label: 'Leadership layer' },
  { key: 'grade', label: 'Grade' },
  { key: 'role', label: 'Role' },
  { key: 'critical', label: 'Critical role' },
]

export const NOT_RECORDED = 'Not recorded'

export const CRITICAL_YES = 'Critical role'
export const CRITICAL_NO = 'Not a critical role'

/** The role a person is counted under: their Role Master role, else their title. */
export function roleOf(p: ReportingPerson): string | null {
  return p.role_title || p.job_title || null
}

/** The value a person carries for one filter, as the filter lists it. */
export function filterValue(p: ReportingPerson, key: FilterKey): string {
  switch (key) {
    case 'role':
      return roleOf(p) || ''
    case 'critical':
      return p.critical_role ? CRITICAL_YES : CRITICAL_NO
    default:
      return (p[key] as string | null) || ''
  }
}

function matches(p: ReportingPerson, f: TalentFilters, skip?: FilterKey): boolean {
  for (const { key } of FILTER_FIELDS) {
    if (key === skip || !f[key]) continue
    if (filterValue(p, key) !== f[key]) return false
  }
  return true
}

export function applyFilters(people: ReportingPerson[], f: TalentFilters): ReportingPerson[] {
  return people.filter((p) => matches(p, f))
}

/** Each filter's choices among the people the OTHER filters leave, so a choice
 *  never leads to an empty screen. */
export function filterOptions(people: ReportingPerson[], f: TalentFilters): Record<FilterKey, string[]> {
  const out = {} as Record<FilterKey, string[]>
  for (const { key } of FILTER_FIELDS) {
    const values = new Set<string>()
    for (const p of people) {
      if (!matches(p, f, key)) continue
      const v = filterValue(p, key)
      if (v) values.add(v)
    }
    out[key] = [...values].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true }))
  }
  return out
}

export function activeFilterCount(f: TalentFilters): number {
  return FILTER_FIELDS.filter(({ key }) => !!f[key]).length
}

/** Whole-number percentage, or null when there is nothing to divide by. */
export function pct(n: number, d: number): number | null {
  return d > 0 ? Math.round((n / d) * 100) : null
}

export function fmtPct(n: number, d: number): string {
  const v = pct(n, d)
  return v === null ? 'n/a' : `${v}%`
}

/** "1 person", "2 people": a count with its noun. */
export function count(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** Role Master's position levels, top down (the August classifications). */
export const LAYER_ORDER = [
  'Manage Business',
  'Manage Function',
  'Manage Managers',
  'Manage Others',
  'Manage Self Professional',
  'Manage Self Administrative',
]

/** The grades people hold, highest first, as HR's deck lists them (E1 before D1 before C3). */
export function gradesHighestFirst(people: ReportingPerson[]): string[] {
  const grades = [...new Set(people.map((p) => (p.grade || '').trim()).filter(Boolean))]
  return grades.sort((a, b) => b.localeCompare(a, 'en', { sensitivity: 'base', numeric: true }))
}

export interface CountRow {
  label: string
  count: number
  /** Share of the people counted, 0 to 100. */
  share: number
}

/** People counted by one attribute, largest first, unrecorded values last
 *  (or in the order given). */
export function countBy(
  people: ReportingPerson[],
  pick: (p: ReportingPerson) => string | null | undefined,
  order?: string[],
): CountRow[] {
  const counts = new Map<string, number>()
  for (const p of people) {
    const label = (pick(p) || '').trim() || NOT_RECORDED
    counts.set(label, (counts.get(label) || 0) + 1)
  }
  const rows = [...counts.entries()].map(([label, count]) => ({
    label,
    count,
    share: people.length ? Math.round((count / people.length) * 100) : 0,
  }))
  const rank = (label: string) => {
    if (label === NOT_RECORDED) return Number.MAX_SAFE_INTEGER
    if (order) {
      const i = order.indexOf(label)
      return i === -1 ? order.length : i
    }
    return 0
  }
  return rows.sort((a, b) => rank(a.label) - rank(b.label) || b.count - a.count || a.label.localeCompare(b.label))
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const MOBILITY_LABEL: Record<string, string> = { yes: 'Yes', maybe: 'Partly', no: 'No' }
export const MOBILITY_ORDER = ['Yes', 'Partly', 'No']

export const PLACEMENT_LABEL: Record<PlacementStatus, string> = {
  placed: 'Placed',
  not_placed: 'Not placed',
  session_open: 'Session still open',
  not_on_a_list: 'Not on a list',
}

/** The same, as the tail of a count: "7 in a session still open". */
export const PLACEMENT_PHRASE: Record<PlacementStatus, string> = {
  placed: 'placed',
  not_placed: 'kept off the grid',
  session_open: 'in a session still open',
  not_on_a_list: 'on no list yet',
}

export const READINESS_SHORT: Record<ReportingReadiness, string> = {
  'ready-now': 'Ready now',
  'ready-1-year': '1 to 3 years',
  'ready-2-plus': '3+ years',
}

export const READINESS_ORDER: ReportingReadiness[] = ['ready-now', 'ready-1-year', 'ready-2-plus']

export function mobilityOf(p: ReportingPerson): string | null {
  return p.mobility ? MOBILITY_LABEL[p.mobility] || p.mobility : null
}

// ---------------------------------------------------------------------------
// View 1: the 9-box
// ---------------------------------------------------------------------------

export interface ScopeSummary {
  inScope: number
  placed: number
  hipos: number
  /** Not placed, by why: session still open, not on a list, kept off the grid. */
  notPlaced: { status: PlacementStatus; label: string; phrase: string; count: number }[]
}

export function scopeSummary(people: ReportingPerson[]): ScopeSummary {
  const placed = people.filter((p) => p.block !== null)
  const notPlaced = (['session_open', 'not_on_a_list', 'not_placed'] as PlacementStatus[])
    .map((status) => ({
      status,
      label: PLACEMENT_LABEL[status],
      phrase: PLACEMENT_PHRASE[status],
      count: people.filter((p) => p.block === null && p.placement_status === status).length,
    }))
    .filter((r) => r.count > 0)
  return {
    inScope: people.length,
    placed: placed.length,
    hipos: people.filter((p) => p.is_hipo).length,
    notPlaced,
  }
}

/** People placed in each block, 1 to 9. */
export function blockCounts(people: ReportingPerson[]): Record<number, number> {
  const out: Record<number, number> = {}
  for (let b = 1; b <= 9; b++) out[b] = 0
  for (const p of people) if (p.block) out[p.block] = (out[p.block] || 0) + 1
  return out
}

// ---------------------------------------------------------------------------
// View 2: high-potentials and their growth
// ---------------------------------------------------------------------------

export interface HipoGrowth {
  hipos: number
  withPlan: number
  /** A next role planned in 1 to 3 years. */
  movePlanned: number
  /** Named a successor for a People Leader's role. */
  successors: number
  /** Ready now for their next role. */
  readyNow: number
}

export function hipoGrowth(people: ReportingPerson[]): HipoGrowth {
  const hipos = people.filter((p) => p.is_hipo)
  return {
    hipos: hipos.length,
    withPlan: hipos.filter((p) => p.has_growth_plan).length,
    movePlanned: hipos.filter((p) => p.next_role_readiness === 'ready-1-year').length,
    successors: hipos.filter((p) => p.is_successor).length,
    readyNow: hipos.filter((p) => p.next_role_readiness === 'ready-now').length,
  }
}

// ---------------------------------------------------------------------------
// View 6: key people
// ---------------------------------------------------------------------------

export function successorsFor(p: ReportingPerson): number {
  const s = p.own_role_successors || { 'ready-now': 0, 'ready-1-year': 0, 'ready-2-plus': 0 }
  return (s['ready-now'] || 0) + (s['ready-1-year'] || 0) + (s['ready-2-plus'] || 0)
}

export interface KeyPeopleSummary {
  keyPeople: number
  inCriticalRoles: number
  withSuccessor: number
  withoutSuccessor: number
}

export function keyPeopleSummary(people: ReportingPerson[]): KeyPeopleSummary {
  const kp = people.filter((p) => p.key_person)
  const withSuccessor = kp.filter((p) => successorsFor(p) > 0).length
  return {
    keyPeople: kp.length,
    inCriticalRoles: kp.filter((p) => p.critical_role).length,
    withSuccessor,
    withoutSuccessor: kp.length - withSuccessor,
  }
}
